import { useI18n } from '@/hooks/useI18n';
import { ArrowUpRight, Download, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { RunSummary, IdentityResponse, TopAccount } from '@/types/api';
import {
  SCHEDULER_RUN_STATUS_LABELS,
  SCHEDULER_RUN_STATUS_LABELS_EN,
  type SchedulerRunStatus,
} from '@/types/enums';
import { formatDate, formatDuration, formatNumber } from '@/lib/format';
import { Chip } from '@/components/primitives/Chip';
import { Dot } from '@/components/primitives/Dot';
import { cn } from '@/lib/cn';

interface ExecutionsActivityProps {
  runs: RunSummary[];
  loading: boolean;
  topAccount: TopAccount | undefined;
  identities: IdentityResponse[] | undefined;
}

export function ExecutionsActivity({
  runs,
  loading,
  topAccount,
  identities,
}: ExecutionsActivityProps) {
  const { t } = useI18n();
  const identityFor = (id: string | null) =>
    id ? (identities?.find((x) => x.id === id) ?? null) : null;
  const topIdentity = identityFor(topAccount?.identityId ?? null);

  return (
    <article className="rounded-[14px] border border-line-1 bg-bg-1 shadow-card">
      <header className="flex flex-col gap-3 border-b border-line-1 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h4 className="text-[13px] font-semibold text-text-1">
            {t('Runs récents', 'Recent runs')}
          </h4>
          <p className="text-[11px] text-text-3">
            {t('Dernières exécutions du scheduler', 'Latest scheduler runs')}
          </p>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <IconChip label={t('Filtrer', 'Filter')}>
            <Filter className="h-3.5 w-3.5" />
          </IconChip>
          <IconChip label={t('Exporter', 'Export')}>
            <Download className="h-3.5 w-3.5" />
          </IconChip>
          {topAccount && (
            <Link
              to={`/accounts/${topAccount.accountId}`}
              className="flex min-h-11 min-w-0 max-w-full flex-wrap items-center gap-1.5 rounded-[8px] border border-line-1 bg-violet-soft px-2 py-1.5 text-[11px] font-medium text-violet-deep transition-colors hover:bg-violet/15 sm:min-h-7 sm:py-0"
            >
              <span className="min-w-0 truncate">Top @{topAccount.username}</span>
              <ArrowUpRight className="h-3 w-3 shrink-0" />
              {topIdentity && (
                <Chip tone="violet" className="max-w-full break-all !px-1.5 !py-0 !text-[10px]">
                  {topIdentity.name}
                </Chip>
              )}
            </Link>
          )}
        </div>
      </header>

      <div className="divide-y divide-line-1/60 sm:hidden">
        {runs.length === 0 && (
          <p className="px-4 py-8 text-center text-[12px] text-text-3">
            {loading ? t('Chargement…', 'Loading…') : t('// aucune exécution', '// no runs')}
          </p>
        )}
        {runs.slice(0, 8).map((run) => (
          <div key={run.runId} className="space-y-3 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-[12px] font-medium text-text-1">
                {run.runId.slice(0, 8)}
              </span>
              <StatusPill status={run.status} />
            </div>
            <dl className="grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <dt className="text-text-3">{t('Durée', 'Duration')}</dt>
                <dd className="mt-0.5 font-mono text-[12px] tabular-nums text-text-2">
                  {formatDuration(run.durationMs)}
                </dd>
              </div>
              <div>
                <dt className="text-text-3">{t('OK / Échec', 'OK / Failed')}</dt>
                <dd className="mt-0.5 font-mono text-[12px] tabular-nums">
                  <span className="text-emerald">{formatNumber(run.accountsOk)}</span>
                  <span className="text-text-4"> / </span>
                  <span className={cn(run.accountsFailed > 0 ? 'text-rose' : 'text-text-3')}>
                    {formatNumber(run.accountsFailed)}
                  </span>
                </dd>
              </div>
              <div className="col-span-2 flex flex-wrap justify-between gap-1">
                <dt className="text-text-3">{t('Horodatage', 'Timestamp')}</dt>
                <dd className="font-mono text-text-2">
                  {formatDate(run.startedAt, 'dd MMM, HH:mm')}
                </dd>
              </div>
            </dl>
          </div>
        ))}
      </div>

      <div className="hidden overflow-x-auto rounded-b-[14px] sm:block">
        <table className="w-full min-w-[560px] text-left">
          <thead>
            <tr className="border-b border-line-1 bg-bg-0/60">
              <th className="w-8 px-4 py-2.5" />
              <th className="px-2 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-3">
                {t('Exécution', 'Run')}
              </th>
              <th className="px-2 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-3">
                {t('Statut', 'Status')}
              </th>
              <th className="px-2 py-2.5 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-text-3">
                {t('Durée', 'Duration')}
              </th>
              <th className="px-2 py-2.5 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-text-3">
                {t('OK / Échec', 'OK / Failed')}
              </th>
              <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-text-3">
                {t('Horodatage', 'Timestamp')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && runs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-[12px] text-text-3">
                  {t('Chargement…', 'Loading…')}
                </td>
              </tr>
            )}
            {!loading && runs.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-10 text-center font-mono text-[11px] text-text-3"
                >
                  {t('// aucune exécution', '// no runs')}
                </td>
              </tr>
            )}
            {runs.slice(0, 8).map((r) => (
              <RunRow key={r.runId} run={r} />
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function RunRow({ run }: { run: RunSummary }) {
  const { t } = useI18n();
  return (
    <tr className="group border-b border-line-1/60 transition-colors last:border-0 hover:bg-bg-2/60">
      <td className="w-8 px-4">
        <span className="flex h-4 w-4 items-center justify-center rounded border border-line-2 bg-bg-1" />
      </td>
      <td className="px-2 py-2.5">
        <div className="text-[12px] font-medium text-text-1">{run.runId.slice(0, 8)}</div>
        <div className="font-mono text-[10px] text-text-4">{t('identifiant', 'identifier')}</div>
      </td>
      <td className="px-2 py-2.5">
        <StatusPill status={run.status} />
      </td>
      <td className="px-2 py-2.5 text-right font-mono text-[12px] tabular-nums text-text-2">
        {formatDuration(run.durationMs)}
      </td>
      <td className="px-2 py-2.5 text-right font-mono text-[12px] tabular-nums text-text-2">
        <span className="text-emerald">{formatNumber(run.accountsOk)}</span>
        <span className="text-text-4"> / </span>
        <span className={cn(run.accountsFailed > 0 ? 'text-rose' : 'text-text-3')}>
          {formatNumber(run.accountsFailed)}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right font-mono text-[11px] text-text-3">
        {formatDate(run.startedAt, 'dd MMM, HH:mm')}
      </td>
    </tr>
  );
}

function StatusPill({ status }: { status: SchedulerRunStatus }) {
  const { t } = useI18n();
  const palette: Record<
    SchedulerRunStatus,
    { tone: 'emerald' | 'violet' | 'rose' | 'amber'; dot: 'ok' | 'running' | 'fail' | 'warn' }
  > = {
    OK: { tone: 'emerald', dot: 'ok' },
    RUNNING: { tone: 'violet', dot: 'running' },
    FAILED: { tone: 'rose', dot: 'fail' },
    PARTIAL: { tone: 'amber', dot: 'warn' },
  };
  const p = palette[status];
  return (
    <Chip tone={p.tone} className="gap-1.5">
      <Dot variant={p.dot} pulse={status === 'RUNNING'} />
      {t(SCHEDULER_RUN_STATUS_LABELS[status], SCHEDULER_RUN_STATUS_LABELS_EN[status])}
    </Chip>
  );
}

function IconChip({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[8px] border border-line-1 bg-bg-1 text-text-3 transition-colors hover:border-line-2 hover:text-text-1 sm:h-7 sm:w-7"
    >
      {children}
    </button>
  );
}
